import { config } from "@app-contexts/sdk/services/config";
import {
  addMessageListener,
  removeMessageListener,
} from "@shared/utils/window/window";
import {
  Widget,
  type WidgetLaunchOptions,
  WidgetQueryParams,
} from "@app-contexts/sdk/services/Widget";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { checkoutWidget } from "./checkout";
import {
  getCustomerAttributionWidgetOptions,
  getEmbeddedWidgetCallArgs,
} from "./embedded-widget-options";
import { CartMessageType } from "../bridge/constants";

/**
 * @deprecated use `checkoutWidget` instead
 * @param options optional host callback used to refresh a rejected attribution token
 */
export async function basketWidget(
  params: WidgetQueryParams,
  options: WidgetLaunchOptions = {}
) {
  const widget = new Widget({
    src: await config.basketWidgetUrl(),
    queryParams: params,
    ...getCustomerAttributionWidgetOptions(params, options),
  });

  widget.show().on("showpass.shopping-cart.checkout", () => {
    widget.close({ removeBodyClass: false });
    checkoutWidget(params, ...getEmbeddedWidgetCallArgs(undefined, options));
  });

  return widget;
}

/**
 * Attaches a listener function for a specific cart message type
 *
 * @param messageType the type of message to listen for
 * @param listener callback function that receives the parsed message data
 * @returns cleanup function to remove the listener
 */
export function addCartMessageListener<T>(
  messageType: CartMessageType,
  listener: (data: T) => void
): () => void {
  const messageHandler = (msg: MessageEvent) => {
    try {
      if (typeof msg.data === "string" && msg.data.startsWith(messageType)) {
        let parsedData: T | undefined;

        switch (messageType) {
          case CartMessageType.COUNT: {
            const count = Number(msg.data.split(".").pop());
            if (!Number.isNaN(count)) {
              parsedData = count as T;
            } else {
              recordAppError(`Invalid ${messageType} value received`);
              return;
            }
            break;
          }
          default:
            recordAppError(`Unknown message type: ${messageType}`);
            return;
        }

        if (parsedData !== undefined) {
          try {
            listener(parsedData);
          } catch (listenerError) {
            // eslint-disable-next-line no-console
            console.error(
              `Error in client-provided ${messageType} listener:`,
              listenerError
            );
          }
        } else {
          recordAppError(`Invalid ${messageType} value received`);
        }
      }
    } catch (error) {
      logAppInfo(`Error processing ${messageType} message`);
      recordAppError(error);
    }
  };

  try {
    addMessageListener(messageHandler);
  } catch (error) {
    logAppInfo(`Error adding ${messageType} listener`);
    recordAppError(error);
  }

  return () => {
    try {
      removeMessageListener(messageHandler);
    } catch (error) {
      logAppInfo(`Error removing ${messageType} listener`);
      recordAppError(error);
    }
  };
}

/**
 * Listener to retrieve the current cart count
 */
export function addCartCountListener(
  listener: (count: number) => void
): () => void {
  return addCartMessageListener<number>(CartMessageType.COUNT, listener);
}
